import {
    CheckCircle2,
    CircleHelp,
    FileText,
    type LucideIcon,
    XCircle,
} from 'lucide-react';
import { useState } from 'react';

import {
    formatOutreachResponse,
    getInboundCallActionPatientLabel,
} from '@apps/web/components/caregivers/caregiver_inbound_call_timeline_utils';
import { ShiftPageDetailsDialog } from '@apps/web/components/dashboard/shift_list_pane';
import { TimelineEventRow } from '@apps/web/components/ui/timeline_event_row';
import { formatCallBadgeLabel } from '@apps/web/components/voice_calls/voice_call_format_utils';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type CallAction = ApiResponseType<
    '/organizations/{organization_id}/voice-calls/{voice_call_id}',
    'get'
>['actions'][number];

// Sentence tail + icon for one recorded action, rendered after the patient
// label ("Jane Doe — shift confirmed").
function getCallActionResult(action: CallAction): {
    readonly Icon: LucideIcon;
    readonly text: string;
} {
    if (action.type === 'shift_confirmation') {
        switch (action.outcome) {
            case 'confirm':
                return { Icon: CheckCircle2, text: 'shift confirmed' };
            case 'decline':
                return { Icon: XCircle, text: 'shift declined' };
            case 'unclear':
                return { Icon: CircleHelp, text: 'needs review' };
            default:
                return {
                    Icon: FileText,
                    text: action.outcome
                        ? formatCallBadgeLabel(action.outcome)
                        : 'shift updated',
                };
        }
    }

    if (action.type === 'outreach_response') {
        switch (action.response) {
            case 'interested':
            case 'available_interested':
                return { Icon: CheckCircle2, text: 'available' };
            case 'not_interested':
            case 'available_not_interested':
            case 'not_a_fit':
            case 'unavailable':
                return { Icon: XCircle, text: 'unavailable' };
            case 'needs_review':
            case 'unknown':
                return { Icon: CircleHelp, text: 'needs review' };
            case 'pending':
            case 'no_response':
                return { Icon: CircleHelp, text: 'response pending' };
            default:
                return {
                    Icon: FileText,
                    text: formatOutreachResponse(action.response),
                };
        }
    }

    return { Icon: FileText, text: 'action recorded' };
}

/**
 * Actions Phoebe recorded on a call, as quiet system-event rows. The patient
 * label opens the shift details dialog when the action is tied to a shift.
 * Transfers are omitted: the transcript already surfaces them as events.
 */
export function CallActionsList({
    organizationId,
    actions,
}: {
    readonly organizationId: string;
    readonly actions: Array<CallAction>;
}) {
    const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
    const visibleActions = actions.filter(
        (action) => action.type !== 'transfer',
    );

    if (visibleActions.length === 0) {
        return null;
    }

    return (
        <div className="space-y-1">
            {visibleActions.map((action, index) => {
                const { Icon, text } = getCallActionResult(action);
                const patientLabel = getInboundCallActionPatientLabel(action);
                return (
                    <TimelineEventRow
                        key={`action-${index}-${action.type}`}
                        icon={<Icon aria-hidden="true" />}
                    >
                        <span className="min-w-0">
                            {action.shift_id ? (
                                <button
                                    className="hover:text-foreground/80 underline-offset-2 transition-colors hover:underline"
                                    onClick={() =>
                                        setSelectedShiftId(
                                            action.shift_id ?? null,
                                        )
                                    }
                                    type="button"
                                >
                                    {patientLabel}
                                </button>
                            ) : (
                                patientLabel
                            )}
                            {` — ${text}`}
                        </span>
                    </TimelineEventRow>
                );
            })}
            <ShiftPageDetailsDialog
                isOpen={selectedShiftId !== null}
                onClose={() => setSelectedShiftId(null)}
                organizationId={organizationId}
                shiftId={selectedShiftId}
            />
        </div>
    );
}
